import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import { useAuth } from '@/auth/AuthContext'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/sidebar'
import { GlobalActivityBar } from '@/components/GlobalActivityBar'
import { AppHeader } from './AppHeader'
import { CommandPalette } from './CommandPalette'
import { NavBrandIcon, SidebarNav } from './SidebarNav'
import { UserMenu } from './UserMenu'

const COOKIE = 'sidebar_state'
const LEGACY_COLLAPSED_KEY = 'mi-sidebar-collapsed'

/**
 * shadcn's sidebar persists open/closed in a cookie it writes itself but reads
 * only through `defaultOpen` (it's built for an SSR framework that reads the
 * cookie on the server). Read it here, falling back to the hand-rolled
 * localStorage key the old shell used so an existing user's collapsed sidebar
 * survives the upgrade.
 */
function readDefaultOpen(): boolean {
  try {
    const cookie = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${COOKIE}=`))
      ?.slice(COOKIE.length + 1)
    if (cookie === 'true') return true
    if (cookie === 'false') return false
    return window.localStorage.getItem(LEGACY_COLLAPSED_KEY) !== '1'
  } catch {
    return true
  }
}

export function AppShell({ children }: { children: ReactNode }) {
  const { me } = useAuth()
  const reduceMotion = useReducedMotion()
  const defaultOpen = useMemo(readDefaultOpen, [])
  const [paletteOpen, setPaletteOpen] = useState(false)

  return (
    <SidebarProvider
      defaultOpen={defaultOpen}
      // The reported bug: the old shell was `flex min-h-screen`, so the whole
      // document scrolled as one unit and the nav rolled off the top. Pinning
      // the frame to the viewport and giving the scroll to the content region
      // alone is the fix — the sidebar and header can no longer move.
      className="h-screen min-h-0 overflow-hidden"
    >
      <Sidebar collapsible="icon" aria-label="Primary navigation">
        <SidebarHeader>
          <Link
            to="/"
            className="flex items-center gap-2 rounded-md px-1 py-1 text-sidebar-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
              <NavBrandIcon className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="truncate text-sm font-semibold tracking-tight group-data-[collapsible=icon]:hidden">
              MedImaging
            </span>
          </Link>
        </SidebarHeader>
        <SidebarSeparator />
        <SidebarContent>
          <SidebarGroup>
            <SidebarNav role={me?.role} />
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <SidebarSeparator />
          <UserMenu me={me} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      <SidebarInset className="h-screen min-w-0 overflow-hidden">
        <AppHeader onOpenPalette={() => setPaletteOpen(true)} />
        {/* Sits above the scroller, not inside it, so a click's progress stays
            visible no matter how far down the page you are. */}
        <GlobalActivityBar />
        {/* The only scroll container in the app. */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/30">
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 4 }}
            animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {children}
          </motion.div>
        </div>
      </SidebarInset>

      <CommandPalette role={me?.role} open={paletteOpen} onOpenChange={setPaletteOpen} />
    </SidebarProvider>
  )
}
